import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info, AlertTriangle } from "lucide-react";
import { Breadcrumbs } from "../Breadcrumbs";
import { CalcInputs } from "./CalcInputs";
import { ResultCard } from "./ResultCard";
import { ScenarioTable } from "./ScenarioTable";
import { SaveCalculation } from "./SaveCalculation";
import { AdSlot } from "../AdSlot";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../ui/accordion";
import { computeByType } from "../../lib/calcEngine";
import { formatCurrency } from "../../lib/format";
import { useSeo, useJsonLd, SITE_URL } from "../../lib/seo";
import { track } from "../../lib/analytics";
import { CALCULATORS } from "../../content/calculators";

const Section = ({ title, children, id }) => (
  <section className="border-t border-border py-8" id={id}>
    <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h2>
    <div className="mt-4 space-y-3 text-base leading-relaxed text-foreground/80">{children}</div>
  </section>
);

export const CalculatorPage = ({ calc, pageSeo, feeConfig }) => {
  const [values, setValues] = useState(
    Object.fromEntries(calc.fields.map((f) => [f.key, String(f.default)]))
  );
  const started = useRef(false);

  const numeric = useMemo(
    () => Object.fromEntries(calc.fields.map((f) => [f.key, parseFloat(values[f.key]) || 0])),
    [values, calc.fields]
  );

  const results = useMemo(() => computeByType(calc.calcType, numeric), [calc.calcType, numeric]);

  const onChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    if (!started.current) {
      started.current = true;
      track("calculator_started", { calculator: calc.slug });
    }
  };

  useEffect(() => {
    track("page_view", { calculator: calc.slug, path: calc.path });
  }, [calc.slug, calc.path]);

  const seo = {
    title: pageSeo?.title || calc.seoTitle || `${calc.name} — ProfitMigo`,
    description: pageSeo?.meta_description || calc.seoDescription,
    path: calc.path,
    robots: pageSeo && pageSeo.indexable === false ? "noindex, follow" : "index, follow",
  };
  useSeo(seo);

  const adsEnabled = !!pageSeo?.ads_enabled;

  useJsonLd("ld-calc", {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
          { "@type": "ListItem", position: 2, name: "Calculators", item: SITE_URL + "/calculators" },
          { "@type": "ListItem", position: 3, name: calc.name, item: SITE_URL + calc.path },
        ],
      },
      {
        "@type": "SoftwareApplication",
        name: calc.name,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
        description: calc.valueProp,
      },
      {
        "@type": "FAQPage",
        mainEntity: calc.content.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  const c = calc.content;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Calculators", to: "/calculators" },
          { label: calc.name },
        ]}
      />

      <header className="max-w-3xl">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">{calc.h1}</h1>
        <p className="mt-4 text-lg text-muted-foreground">{calc.valueProp}</p>
      </header>

      {/* Calculator + result grid */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="mb-4 font-display text-lg font-semibold">Enter your numbers</h2>
            <CalcInputs fields={calc.fields} values={values} onChange={onChange} />
            {calc.feeMarketplace && feeConfig && (
              <p className="mt-5 flex items-start gap-2 rounded-md bg-secondary p-3 text-xs text-muted-foreground" data-testid="fee-assumption-note">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Fee assumptions last updated: {feeConfig.updated_at}. {feeConfig.note}
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="lg:sticky lg:top-20 space-y-6">
            <ResultCard calc={calc} results={results} />
            <ScenarioTable calc={calc} values={numeric} />
            <SaveCalculation calc={calc} values={numeric} results={results} />
          </div>
        </div>
      </div>

      {/* Ads only appear below results, and only if enabled + consented */}
      <AdSlot enabled={adsEnabled} />

      {/* Long-form content */}
      <div className="mt-10 max-w-3xl">
        <Section title="How it works">
          {c.howItWorks.map((p, i) => <p key={i}>{p}</p>)}
        </Section>

        <Section title="Formula & methodology">
          <ul className="space-y-2 font-mono text-sm">
            {c.formula.map((line, i) => (
              <li key={i} className="rounded-md bg-secondary px-3 py-2">{line}</li>
            ))}
          </ul>
        </Section>

        <Section title="Worked example">
          <p className="text-sm text-muted-foreground">{c.workedExample.note}</p>
          <ul className="mt-3 space-y-2 font-mono text-sm">
            {c.workedExample.lines.map((line, i) => (
              <li key={i} className="border-l-2 border-primary/40 pl-3">{line}</li>
            ))}
          </ul>
        </Section>

        <Section title="Important assumptions">
          <ul className="space-y-2">
            {c.assumptions.map((a, i) => (
              <li key={i} className="flex gap-2"><Info className="mt-1 h-4 w-4 shrink-0 text-primary" />{a}</li>
            ))}
          </ul>
        </Section>

        <Section title="Common mistakes">
          <ul className="space-y-2">
            {c.commonMistakes.map((m, i) => (
              <li key={i} className="flex gap-2"><AlertTriangle className="mt-1 h-4 w-4 shrink-0 text-amber-500" />{m}</li>
            ))}
          </ul>
        </Section>

        <Section title="Frequently asked questions">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {c.faq.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left" data-testid={`faq-trigger-${i}`}>{item.q}</AccordionTrigger>
                <AccordionContent>{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>
      </div>

      {/* Related calculators */}
      <section className="border-t border-border py-10">
        <h2 className="font-display text-2xl font-semibold tracking-tight">Related calculators</h2>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {calc.related.map((slug) => {
            const rc = CALCULATORS[slug];
            if (!rc) return null;
            return (
              <Link
                key={slug}
                to={rc.path}
                data-testid={`related-${slug}`}
                className="group flex items-center justify-between rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary"
              >
                <span className="font-medium">{rc.name}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
              </Link>
            );
          })}
        </div>
      </section>

      <div className="rounded-xl border border-border bg-accent/40 p-6 text-sm text-muted-foreground">
        These results are estimates based only on the numbers you entered. Actual fees, taxes and returns vary and can change over time. Verify important pricing decisions independently. Current example net profit: {formatCurrency(results.netProfit)}.
      </div>
    </div>
  );
};
